import OrderApi from '@apis/order/OrderApi';
import queries from '@apis/queries';
import useFingerPay from '@hooks/order/useFingerPay';
import useNicePay from '@hooks/order/useNicePay';
import usePayU from '@hooks/order/usePayU';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { CreateOrderRequest, CreateOrderResponse } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@router/stackflow';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import useTossPg from './useTossPg';

function usePrepareOrder() {
    const state = useCalculatedRequest();
    const projectId = useProjectId();
    const project = useProject();
    const [_, setCookie] = useCookies(['shopId', 'shopName', 'tempOrder']);
    const toast = useToast();
    const { push, pop } = useFlow();
    const queryClient = useQueryClient();
    const { startLoading, stopLoading } = useLoading();
    const openFingerPay = useFingerPay();
    const openPayU = usePayU();
    const openNicePay = useNicePay();
    const { t } = useTranslation('order');
    const { handleTossPg } = useTossPg();

    const handleKicc = (checkoutUrl: string) => {
        setCookie('shopId', state.shopId, { path: '/' });
        setCookie('shopName', state.shopName, { path: '/' });
        stopLoading();
        window.location.href = checkoutUrl;
    };
    const generateItemName = () => {
        if (state.orderLines.length === 1) {
            return state.orderLines?.[0].itemName;
        }
        return t('pgItemName.andMore', {
            name: state.orderLines?.[0].itemName,
            count: state.orderLines.length - 1,
        });
    };

    const handleFingerPay = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' | 'returnUrl'>>,
    ) => {
        openFingerPay({
            ...response,
            paymentAmount: orderWithTotalAmount(state).paymentAmount,
            itemName: generateItemName(),
            returnUri: response.returnUrl,
        });
        stopLoading();
    };

    const handleNicePay = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' | 'returnUrl'>>,
    ) => {
        openNicePay({
            ...response,
            paymentAmount: orderWithTotalAmount(state).paymentAmount,
            itemName: generateItemName(),
            returnUri: response.returnUrl,
        });
        stopLoading();
    };

    const handleEasyCard = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' | 'returnUrl' >>
        &Pick<CreateOrderResponse, 'approvalMallId'>,
    ) => {
        const {
            orderNo, requestedAt, hashString, approvalMallId, returnUrl,
        } = response;
        stopLoading();
        openPayU({
            orderNo,
            requestedAt,
            paymentAmount: orderWithTotalAmount(state).paymentAmount,
            itemName: generateItemName(),
            approvalMallId,
            hashString,
            returnUrl,
        });
    };

    return async () => {
        startLoading();
        const request: CreateOrderRequest = { ...orderWithTotalAmount(state) };
        request.returnUrl = `${import.meta.env.VITE_BASE_URL}/purchase/complete`;
        console.log({ request });
        request.discounts = request.discounts || [];
        setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
        try {
            // 주문금액 0원일 경우 pg 미사용
            if (state.paymentAmount === 0) {
                const { orderNo } = await OrderApi.createFreeOrder({ ...request, projectId });
                queryClient.invalidateQueries({ queryKey: queries.orders._def });
                queryClient.invalidateQueries({ queryKey: queries.user._def });
                queryClient.invalidateQueries({ queryKey: queries.coupons._def });
                queryClient.invalidateQueries({ queryKey: queries.orderLog._def });
                queryClient.resetQueries({ queryKey: queries.cart._def });
                pop(4, { animate: false });
                push('OrderLogsActivity', { duration: 'ALL' }, { animate: false });
                push('OrderLogActivity', { orderNo });
                stopLoading();
            } else {
                const res = await OrderApi.createOrder({ ...request, projectId });
                const {
                    checkoutUrl, orderNo, mallId, requestedAt, hashString, approvalMallId, returnUrl,
                } = res;

                if (checkoutUrl) {
                    console.log('checkout url', checkoutUrl);
                    handleKicc(checkoutUrl);
                } else if (project?.pgVendor === 'TOSS' && orderNo) {
                    console.log('toss pg');
                    handleTossPg(orderNo);
                } else if (orderNo && mallId && requestedAt && hashString && returnUrl) {
                    if (state.paymentMethod === 'EASY_CARD') {
                        console.log('간편결제 사용');
                        handleEasyCard({
                            orderNo, mallId, requestedAt, hashString, approvalMallId, returnUrl,
                        });
                    } else if (project?.pgVendor === 'FINGERPAY') {
                        console.log('핑거페이');
                        handleFingerPay({
                            orderNo, mallId, requestedAt, hashString, returnUrl,
                        });
                    } else if (project?.pgVendor === 'NICEPAY') {
                        console.log('나이스페이');
                        handleNicePay({
                            orderNo, mallId, requestedAt, hashString, returnUrl,
                        });
                    }
                } else {
                    console.error('주문 실패 data:', res);
                    toast.error('올바르지 않은 주문이 생성되었습니다.');
                }
            }
        } catch (e) {
            const axiosError = e as AxiosError<ErrorResponse>;
            console.error(e);
            stopLoading();
            switch (axiosError.response?.data?.code) {
            case 'INVALID_PAYMENT_AMOUNT':
                toast.error(t('toast.invalidPaymentAmount'));
                break;
            default:
                toast.error(t('toast.orderError'));
            }
        }
    };
}

export default usePrepareOrder;
